import { inject, Injectable } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { FacebookLoginResponse } from '@luggagehero/shared/interfaces';
import { SharedScriptService } from '@luggagehero/shared/services/script';
import { firstValueFrom } from 'rxjs';

declare const FB: Facebook;

interface FacebookLoginOptions {
  scope?: string;
  return_scopes?: boolean;
}

interface Facebook {
  init(config: { appId: string; cookie: boolean; xfbml: boolean; version: string }): void;
  logout(callback: () => void): void;
  login(callback: (response: FacebookLoginResponse) => void, options: FacebookLoginOptions): void;
}

@Injectable()
export class FacebookSdk {
  protected scriptService = inject(SharedScriptService);

  private initialized = false;
  /** The one in-flight load, so concurrent callers (a double tap) share it; cleared on failure to allow a retry. */
  private loading: Promise<Facebook> | null = null;

  public load(): Promise<Facebook> {
    this.loading ??= this.loadOnce().catch((err: unknown) => {
      this.loading = null;
      throw err;
    });
    return this.loading;
  }

  /**
   * Start loading the SDK as a sign-in view opens, so FB.login() runs inside the tap rather than after an awaited
   * download, which browsers block as a popup (AM-5110). Nothing to do where the SDK is already on the page.
   */
  public preload(): void {
    if (typeof FB === 'undefined') {
      void this.load().catch(() => undefined);
    }
  }

  private async loadOnce(): Promise<Facebook> {
    // ionic-traveler still loads the SDK in its index.html; web-traveler loads it here, on first use (AM-5110)
    if (typeof FB === 'undefined') {
      await firstValueFrom(this.scriptService.load('facebook'));
    }

    if (!this.initialized) {
      FB.init({
        appId: Config.environment.FACEBOOK_APP_ID,
        cookie: false, // enable cookies to allow the server to access the session
        xfbml: true, // parse social plugins on this page
        version: 'v8.0', // use graph api version 8.0
      });
      this.initialized = true;
    }
    return FB;
  }
}
